<template>
  <el-dialog id="manageCardGroupDialog" :title="$t('text.manageCardGroup')" :visible="show" :close-on-click-modal="false" width="600px" @open="handleDialogOpen" @close="handleDialogClose">
    <el-empty v-if="noGroups" v-show="noGroups && !addMode" :description="$t('message.noCardGroups')"/>
    <el-row v-else v-for="group in groups">
      <el-col :span="16">
        <el-form :model="groups[group.id]" :rules="rules" :ref="`form:${group.id}`">
          <el-form-item prop="name">
            <el-input v-model="group.name" :disabled="inputsDisabled[group.id]" :placeholder="$t('message.inputCardGroupName')"/>
          </el-form-item>
        </el-form>
      </el-col>
      <el-col class="actions" :span="8" v-show="actionsShow[group.id]">
        <el-button type="primary" @click="handleEditClick(group.id)">{{ $t('text.edit') }}</el-button>
        <el-button type="danger" @click="handleDeleteClick(group.id)">{{ $t('text.delete') }}</el-button>
      </el-col>
      <el-col class="actions" :span="8" v-show="confirmSavingShow[group.id]">
        <el-button type="primary" @click="handleSaveClick(group.id)">{{ $t('text.save') }}</el-button>
        <el-button @click="handleCancelClick">{{ $t('text.cancel') }}</el-button>
      </el-col>
      <el-col class="actions" :span="8" v-show="confirmDeletingShow[group.id]">
        <el-button type="danger" @click="handleConfirmDeleteClick(group.id)">{{ $t('text.confirm') }}</el-button>
        <el-button @click="handleCancelClick">{{ $t('text.cancel') }}</el-button>
      </el-col>
    </el-row>
    <el-row v-show="addMode">
      <el-col :span="16">
        <el-form :model="newGroup" :rules="rules" ref="form:new">
          <el-form-item prop="name">
            <el-input v-model="newGroup.name" :placeholder="$t('message.inputCardGroupName')"/>
          </el-form-item>
        </el-form>
      </el-col>
      <el-col class="actions" :span="8">
        <el-button type="primary" @click="handleSaveClick('new')">{{ $t('text.save') }}</el-button>
        <el-button @click="handleCancelClick">{{ $t('text.cancel') }}</el-button>
      </el-col>
    </el-row>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleCloseClick">{{ $t('text.close') }}</el-button>
      <el-button type="primary" :disabled="addMode" @click="handleAddClick">{{ $t('text.addCardGroup') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "BankCardGroupEdit",
  props: {
    show: Boolean,
  },
  data() {
    return {
      groups: {},
      newGroup: {},
      addMode: false,
      targetGroup: null,
      inputsDisabled: {},
      actionsShow: {},
      confirmSavingShow: [],
      confirmDeletingShow: {},
      rules: {
        name: [
          {required: true, message: this.$t("message.inputCardGroupName"), trigger: "blur"},
          {max: 20, message: this.$t("message.lengthMax20Error"), trigger: "blur"}
        ]
      },
      loading: null
    }
  },
  computed: {
    noGroups() {
      return Object.keys(this.groups).length === 0;
    }
  },
  methods: {
    handleDialogOpen() {
      this.refreshData();
    },
    handleDialogClose() {
      this.closeDialog();
    },
    handleAddClick() {
      this.addMode = true;
      this.resetAndHideTargetGroupEditor("new");

    },
    handleEditClick(groupId) {
      this.$set(this.inputsDisabled, groupId, false);
      this.$set(this.actionsShow, groupId, false);
      this.$set(this.confirmSavingShow, groupId, true);

      this.resetAndHideTargetGroupEditor(groupId);
    },
    handleDeleteClick(groupId) {
      this.$set(this.actionsShow, groupId, false);
      this.$set(this.confirmDeletingShow, groupId, true);

      this.resetAndHideTargetGroupEditor(groupId);
    },
    handleConfirmDeleteClick(groupId) {
      this.lockDialog();
      const group = this.groups[groupId];
      this.$axios
          .post("/bankCardInfo/bankCardGroupInfoDelete", group)
          .then((res) => {
            this.unlockDialog();
            if (res.code === 200) {
              this.$notify.success({
                title: this.$t("text.prompt"),
                message: res.msg
              })
              this.refreshData();
              return true;
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              })
              return false;
            }
          })
          .catch(() => {
            this.unlockDialog();
          });
    },
    handleSaveClick(groupId) {
      this.getFormRef(groupId).validate((valid) => {
        if (valid) {
          this.lockDialog();
          const url = groupId === "new" ? "/bankCardInfo/bankCardGroupInfoAdd" : "/bankCardInfo/bankCardGroupInfoUpdate";
          const group = groupId === "new" ? this.newGroup : this.groups[groupId];

          this.$axios
              .post(url, group)
              .then((res) => {
                this.unlockDialog();
                if (res.code === 200) {
                  this.$notify.success({
                    title: this.$t("text.prompt"),
                    message: res.msg
                  });
                  this.refreshData();
                  return true;
                } else {
                  this.$notify.error({
                    title: this.$t("text.prompt"),
                    message: res.msg
                  });
                  return false;
                }
              })
              .catch(() => {
                this.unlockDialog();
              });
        } else {
          return false;
        }
      });
    },
    handleCancelClick() {
      this.resetAndHideTargetGroupEditor(null);
    },
    handleCloseClick() {
      this.closeDialog();
    },
    closeDialog() {
      this.resetAndHideTargetGroupEditor(null);
      this.$emit("update:show", false);
    },
    lockDialog() {
      this.loading = this.$loading({
        lock: true,
        text: this.$t("text.loading"),
        spinner: "el-icon-loading",
        target: "#manageCardGroupDialog .el-dialog"
      })
    },
    unlockDialog() {
      if (this.loading !== null) {
        this.loading.close();
      }
    },
    refreshData() {
      this.lockDialog();
      this.$axios
          .get("/bankCardInfo/selectBankCardGroupInfoList", {params: {}})
          .then((res) => {
            if (res.code === 200) {
              this.buildGroupList(res.data);
              this.resetAndHideTargetGroupEditor(null);
            }
            this.unlockDialog();
          })
          .catch(() => {
            this.unlockDialog();
          });
    },
    resetAndHideTargetGroupEditor(groupId) {
      if (this.targetGroup === null) {
        this.targetGroup = groupId;
        return;
      }

      if (this.targetGroup === "new") {
        this.addMode = false;
      } else {
        this.$set(this.inputsDisabled, this.targetGroup, true);
        this.$set(this.actionsShow, this.targetGroup, true);
        this.$set(this.confirmSavingShow, this.targetGroup, false);
        this.$set(this.confirmDeletingShow, this.targetGroup, false);
      }

      this.getFormRef(this.targetGroup).resetFields();
      this.targetGroup = groupId;
    },
    getFormRef(groupId) {
      const formRefs = this.$refs[`form:${groupId}`];
      return Array.isArray(formRefs) ? formRefs[0] : formRefs;
    },
    buildGroupList(groups) {
      this.newGroup = {
        id: null,
        name: null
      };

      this.groups = {};
      this.inputsDisabled = {};
      this.actionsShow = {};
      this.confirmSavingShow = {};
      this.confirmDeletingShow = {};
      groups.forEach((item) => {
        this.$set(this.groups, item.id, item);
        this.$set(this.inputsDisabled, item.id, true);
        this.$set(this.actionsShow, item.id, true);
        this.$set(this.confirmSavingShow, item.id, false);
        this.$set(this.confirmDeletingShow, item.id, false);
      });
    }
  }
}
</script>

<style scoped>
#manageCardGroupDialog .actions {
  text-align: center;
}
</style>